/**
 * UndoSendService - Manages email sending with undo capability
 *
 * This service queues emails for sending with a configurable delay,
 * allowing users to cancel (undo) the send before it actually happens.
 */

import EmailService from './EmailService';

class UndoSendService {
  constructor() {
    this.queue = new Map(); // Map of queueId -> { emailData, timeoutId, status }
    this.listeners = new Set(); // Set of callback functions
    this.defaultDelay = 5000; // 5 seconds default delay
  }

  /**
   * Queue an email for sending
   * @param {Object} emailData - The email data to send
   * @param {number} delayMs - Delay in milliseconds before sending
   * @returns {string} - Queue ID that can be used to cancel
   */
  queueEmail(emailData, delayMs = this.defaultDelay) {
    const queueId = `email-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const timeoutId = setTimeout(() => {
      this.executeSend(queueId);
    }, delayMs);

    this.queue.set(queueId, {
      emailData,
      timeoutId,
      status: 'queued',
      queuedAt: Date.now(),
      delayMs,
    });

    this.notifyListeners({ type: 'queued', queueId, emailData });

    return queueId;
  }

  /**
   * Cancel a queued email (undo send)
   * @param {string} queueId - The queue ID to cancel
   * @returns {Object|null} - The cancelled email data, or null if not found
   */
  cancelSend(queueId) {
    const queuedEmail = this.queue.get(queueId);

    if (!queuedEmail || queuedEmail.status !== 'queued') {
      console.warn(`Cannot cancel email ${queueId}: not found or already processed`);
      return null;
    }

    // Clear the timeout
    clearTimeout(queuedEmail.timeoutId);

    // Update status
    queuedEmail.status = 'cancelled';
    this.queue.set(queueId, queuedEmail);

    this.notifyListeners({
      type: 'cancelled',
      queueId,
      emailData: queuedEmail.emailData,
    });

    // Clean up after a delay
    setTimeout(() => {
      this.queue.delete(queueId);
    }, 1000);

    return queuedEmail.emailData;
  }

  /**
   * Execute the actual send
   * @param {string} queueId - The queue ID to send
   */
  async executeSend(queueId) {
    const queuedEmail = this.queue.get(queueId);

    if (!queuedEmail || queuedEmail.status !== 'queued') {
      console.warn(`Cannot send email ${queueId}: not found or already processed`);
      return;
    }

    queuedEmail.status = 'sending';
    this.queue.set(queueId, queuedEmail);

    try {
      const result = await EmailService.sendEmail(queuedEmail.emailData);

      queuedEmail.status = 'sent';
      queuedEmail.result = result;
      this.queue.set(queueId, queuedEmail);

      this.notifyListeners({
        type: 'sent',
        queueId,
        emailData: queuedEmail.emailData,
        result,
      });

      // Clean up after a delay
      setTimeout(() => {
        this.queue.delete(queueId);
      }, 5000);

      return result;
    } catch (error) {
      console.error('Failed to send email:', error);

      queuedEmail.status = 'failed';
      queuedEmail.error = error;
      this.queue.set(queueId, queuedEmail);

      this.notifyListeners({
        type: 'failed',
        queueId,
        emailData: queuedEmail.emailData,
        error,
      });

      throw error;
    }
  }

  /**
   * Get the status of a queued email
   * @param {string} queueId - The queue ID to check
   * @returns {Object|null} - The queued email data, or null if not found
   */
  getStatus(queueId) {
    return this.queue.get(queueId) || null;
  }

  /**
   * Get all queued emails
   * @returns {Array} - Array of queued email data
   */
  getQueuedEmails() {
    return Array.from(this.queue.entries())
      .filter(([, data]) => data.status === 'queued')
      .map(([id, data]) => ({ id, ...data }));
  }

  /**
   * Add a listener for queue events
   * @param {Function} callback - Callback function
   */
  addListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * Remove a listener
   * @param {Function} callback - Callback function
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of an event
   * @param {Object} event - Event data
   */
  notifyListeners(event) {
    this.listeners.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in UndoSendService listener:', error);
      }
    });
  }

  /**
   * Set the default delay
   * @param {number} delayMs - Delay in milliseconds
   */
  setDefaultDelay(delayMs) {
    this.defaultDelay = delayMs;
  }

  /**
   * Get the default delay
   * @returns {number} - Delay in milliseconds
   */
  getDefaultDelay() {
    return this.defaultDelay;
  }
}

// Export a singleton instance
const undoSendService = new UndoSendService();
export default undoSendService;
